import { useCallback } from 'react'

import { useAuthedFetch } from '../../auth/useAuthedFetch'
import withFetchCancel from '../../libs/withFetchCancel'
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from 'react-query'
import {
  transformFiltersToQueryParams,
  parseFullTrackingResponse,
  parseShippingDetailResponse,
  parseShippingSearchResponse,
  parseShippingsTrackingResponse,
  parseShippingStatesResponse,
} from './business'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

const SHIPPINGS_TRACKING_KEY = 'shippingsTracking'

const useShippingsTracking = ({
  filters,
  type = 'shippings',
  statusList,
  config = {},
  allowCancelled,
  getAllPages,
}) => {
  if (
    type !== 'shippings' &&
    type !== 'incidents' &&
    type !== 'returns' &&
    type !== 'unscheduledPickups'
  )
    throw new Error(`Programming error: Unknown tracking type "${type}"`)

  const authedFetch = useAuthedFetch()

  // NOTE: la clave de la query de seguimiento de envíos o incidencias
  // tiene que estar formada por los filtros que atañen a cada tipo de
  // envío (envío normal o con incidencias) junto con el resto de filtros
  // comunes que el usuario puede aplicar.
  const commonFiltersKeyFragment = {
    search: filters.search,
    centres: filters.centres,
    country: filters.country,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    destinPostalCode: filters.destinPostalCode,
    originPostalCode: filters.originPostalCode,
    postalCodeFrom: filters.postalCodeFrom,
    postalCodeTo: filters.postalCodeTo,
    serviceType: filters.serviceType,
    clientCode: filters.clientCode,
    clientCenterCode: filters.clientCenterCode,
    hasIncident: filters.hasIncident,
  }
  const specificFiltersKeyFragment =
    type === 'shippings'
      ? {
          shippingsPage: filters.shippingsPage,
          shippingsRowsPerPage: filters.shippingsRowsPerPage,
          shippingsSortColumn: filters.shippingsSortColumn,
          shippingsSortType: filters.shippingsSortType,
          shippingCode: filters.shippingCode,
          statusCode: filters.statusCode,
        }
      : type === 'incidents'
      ? {
          incidentsPage: filters.incidentsPage,
          incidentsRowsPerPage: filters.incidentsRowsPerPage,
          incidentsSortColumn: filters.incidentsSortColumn,
          incidentsSortType: filters.incidentsSortType,
          incidentCode: filters.incidentCode,
        }
      : type === 'returns'
      ? {
          returnsPage: filters.returnsPage,
          returnsRowsPerPage: filters.returnsRowsPerPage,
          returnsSortColumn: filters.returnsSortColumn,
          returnsSortType: filters.returnsSortType,
          shippingCode: filters.shippingCode,
        }
      : type === 'unscheduledPickups'
      ? 'unscheduledPickups'
      : null

  const queryKey = [
    SHIPPINGS_TRACKING_KEY,
    type,
    commonFiltersKeyFragment,
    specificFiltersKeyFragment,
  ]

  const query = useQuery(
    queryKey,
    async () =>
      getAllPages
        ? await getAllShippingsPages(authedFetch, filters, allowCancelled)
        : await getOneShippingsPage(authedFetch, filters, allowCancelled),
    {
      select: ({ data }) =>
        parseShippingsTrackingResponse(data, statusList, type),
      ...config,
    },
  )

  return query
}

const getOneShippingsPage = async (authedFetch, filters, allowCancelled) => {

  const queryParams = transformFiltersToQueryParams(filters, allowCancelled)
  
  if (filters?.centres?.length) {
    return await withFetchCancel(signal =>
      authedFetch(
        'GET',
        `${BACKEND_ENDPOINT}/shippings?mapping_table_code=WEBCLIENT&${queryParams}`,
        { parseJSON: true, signal },
      ),
    )
  } else {
    return {
      data: {
        data: [],
        pagination: {
          page_limit: 0,
          record_count: 0,
          page_offsets: {
            current: 0,
            next: 0,
            last: 0,
          },
        },
      },
    }
  }
}

const getAllShippingsPages = async (authedFetch, filters, allowCancelled) => {
  let currentResponse
  const allResponses = {
    data: [],
    pagination: { page_offsets: { current: 0, last: 1 } },
  }
  while (
    allResponses.pagination.page_offsets.current <
    allResponses.pagination.page_offsets.last
  ) {
    const newFilters = {
      ...filters,
      page: allResponses.pagination?.page_offsets.current,
    }
    currentResponse = await getOneShippingsPage(
      authedFetch,
      newFilters,
      allowCancelled,
    )
    allResponses.data = [...allResponses.data, ...currentResponse.data.data]
    allResponses.pagination = currentResponse.data.pagination
  }
  return { data: allResponses, res: currentResponse }
}

const useInfiniteShippingsTracking = (
  filters,
  statusList,
  config = {},
  extraKey,
) => {
  const authedFetch = useAuthedFetch()

  const fetchShippings = useCallback(
    async ({ pageParam = 1 }) => {
      const queryParams = transformFiltersToQueryParams(filters)

      return await withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/shippings?${queryParams}&page_limit=15&page_offsets=${pageParam}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      )
    },
    [authedFetch, filters],
  )

  const query = useInfiniteQuery({
    queryKey: [SHIPPINGS_TRACKING_KEY, extraKey, filters],
    queryFn: fetchShippings,
    select: data => ({
      pages: data.pages.flatMap(x => x),
      pageParams: data.pageParams,
    }),
    getNextPageParam: (lastPage = {}) => {
      if (!lastPage?.data) return undefined
      const { current, last, next } = lastPage.data.pagination.page_offsets
      if (current === last) return undefined
      return next
    },
    ...config,
  })

  return query
}

const useFullTracking = (shippingCode, config = {}) => {
  const authedFetch = useAuthedFetch()
  const query = useQuery(
    ['simplifiedTracking', shippingCode],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/item-history-api/private/v1/history/${shippingCode}?view=WEBCLIENT&show_items=false`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => parseFullTrackingResponse(data),
      useErrorBoundary: false,
      enabled: shippingCode !== undefined && shippingCode !== null,
      ...config,
    },
  )

  return query
}

const useShippingSearchByCode = (shippingCode, statusList, config = {}) => {
  const authedFetch = useAuthedFetch()

  const query = useQuery(
    ['shippingSearch', { shippingCode }],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/shippings/${shippingCode}?mapping_table_code=WEBCLIENT`,
          {
            parseJSON: true,
            signal,
            throwOnError: false,
          },
        ),
      ),
    {
      select: ({ data }) => parseShippingSearchResponse(data, statusList),
      enabled: shippingCode !== '',
      ...config,
    },
  )

  return query
}

const useShippingCancelMutation = options => {
  const authedFetch = useAuthedFetch()
  const queryClient = useQueryClient()

  return useMutation(
    shippingCode =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/rpc-cancel-shipping-by-shipping-code/${shippingCode}`,
          {
            parseJSON: true,
            signal,
            data: {
              metadata: {
                interchange_id: null,
                timestamp: null,
                correlation_id: null,
                source: null,
              },
              audit: {
                process_code: 'BROKER',
              },
            },
          },
        ),
      ),
    {
      ...options,
      onSettled: (...params) => {
        queryClient.invalidateQueries(SHIPPINGS_TRACKING_KEY)
      },
    },
  )
}

const useShippingDetail = (shippingId, states, statusList) => {
  const authedFetch = useAuthedFetch()

  const query = useQuery(
    ['shippingDetail', shippingId],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/shippings/${shippingId}?mapping_table_code=WEBCLIENT`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => parseShippingDetailResponse(data, states, statusList),
    },
  )

  return query
}

const useInvalidateShippingsDetail = () => {
  const queryClient = useQueryClient()
  return shippingId =>
    queryClient.invalidateQueries(['shippingDetail', shippingId])
}

const useShippingStates = (shippingCodes, config = {}) => {
  const authedFetch = useAuthedFetch()

  const fetchShippingStates = useCallback(async () => {
    if (!shippingCodes || shippingCodes.length === 0) return

    // When we request shipments without scheduled pickup, the API can return thousands of shipments.
    // v1/history endpoint is a GET request and has a maximum limit for the URL size.
    // We must divide the request into groups and make several requests in parallel.

    const shippingCodesMaxSizeGroup = 25
    const shippingCodesGroups = []

    while (shippingCodes.length > 0) {
      shippingCodesGroups.push(
        shippingCodes.splice(0, shippingCodesMaxSizeGroup),
      )
    }

    const requests = shippingCodesGroups.map(async shippingCodesGroup => {
      const shippingCodesParam = shippingCodesGroup.join('%2C')
      const url = `${BACKEND_ENDPOINT}/item-history-api/private/v1/history?shipping_codes=${shippingCodesParam}&view=WEBCLIENT&show_items=true&page_index=0&page_size=${shippingCodesGroup.length}`
      return await withFetchCancel(signal =>
        authedFetch('GET', url, {
          parseJSON: true,
          signal,
        }),
      )
    })

    return await Promise.all(requests).then(responses => {
      const data = responses.reduce(
        (acc, response) => acc.concat(response.data.data),
        [],
      )
      return { data: { data } }
    })
  }, [authedFetch, shippingCodes])

  return useQuery(['shippingStates', shippingCodes], fetchShippingStates, {
    select: ({ data }) => parseShippingStatesResponse(data),
    enabled: !!shippingCodes && shippingCodes.length > 0,
    ...config,
  })
}

export {
  useShippingsTracking,
  useInfiniteShippingsTracking,
  useFullTracking,
  useShippingSearchByCode,
  useShippingCancelMutation,
  useShippingDetail,
  useInvalidateShippingsDetail,
  useShippingStates,
}